import { Card, CreateCardDto, PatchCardDto, Shop } from "@/types/v2";
import { CardMenuDialog } from "@/hooks/useCardMenuActions";
import { CardFormDialog } from "@/components/Dashboard/components/Card/CardFormDialog";
import { CardTagsDialog } from "@/components/Dashboard/components/Card/CardTagsDialog";
import { CardRemoveDialog } from "@/components/Dashboard/components/Card/CardRemoveDialog";
import { CardMarkCompleteDialog } from "@/components/Dashboard/components/Card/CardMarkCompleteDialog";
import { CustomerUpdatesDialog } from "@/components/CustomerUpdate/CustomerUpdatesDialog";
import { CustomerUpdatesTimelineDialog } from "@/components/CustomerUpdate/CustomerUpdatesTimelineDialog";
import { CardForceEffectDialog } from "@/components/Dashboard/components/Card/CardForceEffectDialog";
import { getDefaultForceEffectColor } from "@/lib/cards";
import { DEFAULT_COLORS } from "@/lib/const";

export interface CardActionDialogsProps {
  cardData: Card;
  shop: Shop;
  dialogOpen: boolean;
  dialog: CardMenuDialog;
  setDialogOpen: (open: boolean) => void;
  handleEditCard: (values: PatchCardDto) => Promise<void>;
  handleEditTag: (values: { tags: string[] }) => Promise<void>;
  handleRemoveCard: () => Promise<void>;
  handleMarkComplete: () => Promise<void>;
  handleForceEffect: (
    action: "flash" | "wiggle",
    color: string
  ) => Promise<void>;
  duplicatePrefill: CreateCardDto;
  handleDuplicateCard: (values: CreateCardDto) => Promise<void>;
}

export const CardActionDialogs = ({
  cardData,
  shop,
  dialogOpen,
  dialog,
  setDialogOpen,
  handleEditCard,
  handleEditTag,
  handleRemoveCard,
  handleMarkComplete,
  handleForceEffect,
  duplicatePrefill,
  handleDuplicateCard,
}: CardActionDialogsProps) => (
  <>
    <CardFormDialog
      title="Edit"
      buttonTitle="Edit Card"
      description="Edit card. Card fields are determined by the card configuration."
      cardData={cardData}
      onSubmit={handleEditCard}
      externalTrigger
      open={dialogOpen && dialog === "form"}
      onOpenChange={setDialogOpen}
    />
    <CardFormDialog
      title="Duplicate Card"
      buttonTitle="Duplicate Card"
      description="Review and adjust the duplicate before creating it. Card fields are determined by the card configuration."
      initialValues={duplicatePrefill}
      onSubmit={handleDuplicateCard}
      externalTrigger
      open={dialogOpen && dialog === "duplicate"}
      onOpenChange={setDialogOpen}
    />
    <CardTagsDialog
      onSubmit={handleEditTag}
      cardData={cardData}
      externalTrigger
      open={dialogOpen && dialog === "tags"}
      onOpenChange={setDialogOpen}
    />
    <CardRemoveDialog
      onRemove={handleRemoveCard}
      externalTrigger
      open={dialogOpen && dialog === "remove"}
      onOpenChange={setDialogOpen}
      cardSource={cardData.source}
      integrator={shop.integration}
    />
    <CardMarkCompleteDialog
      onMarkComplete={handleMarkComplete}
      externalTrigger
      open={dialogOpen && dialog === "markComplete"}
      onOpenChange={setDialogOpen}
      cardSource={cardData.source}
      integrator={shop.integration}
    />
    <CustomerUpdatesDialog
      cardData={cardData}
      shop={shop}
      externalTrigger
      open={dialogOpen && dialog === "customerUpdate"}
      onOpenChange={setDialogOpen}
    />
    <CustomerUpdatesTimelineDialog
      cardData={cardData}
      shop={shop}
      externalTrigger
      open={dialogOpen && dialog === "customerUpdatesTimeline"}
      onOpenChange={setDialogOpen}
    />
    <CardForceEffectDialog
      action="flash"
      defaultColor={getDefaultForceEffectColor(shop)}
      cardColors={shop.cardConfig?.cardColors ?? DEFAULT_COLORS}
      onSelect={(color) => handleForceEffect("flash", color)}
      externalTrigger
      open={dialogOpen && dialog === "flashColor"}
      onOpenChange={setDialogOpen}
    />
  </>
);
